import { useRef, useEffect, useState } from "react";

/**
 * HeroVideo — cinematic looping video (desktop) or a static poster (mobile).
 * On mobile devices we skip the video + Ken Burns + film-grain entirely
 * because they cause scroll-flash and repaint storms on iOS Safari.
 */
function useIsMobile() {
  const [m, setM] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(pointer: coarse), (max-width: 1023px)");
    const on = () => setM(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return m;
}

export default function HeroVideo({
  sources = [],
  poster,
  className = "",
  overlayClassName = "bg-gradient-to-t from-cream-100 via-cream-100/20 to-transparent",
}) {
  const videoRef = useRef(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isMobile) return; // no video on mobile
    const v = videoRef.current;
    if (!v) return;
    const tryPlay = async () => {
      try { await v.play(); } catch { /* ignored */ }
    };
    tryPlay();
  }, [isMobile]);

  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`}>
      {/* Poster — static on mobile, Ken Burns on desktop */}
      {poster && (
        <img
          src={poster}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={!isMobile ? { animation: "kenburns 24s ease-in-out infinite alternate" } : undefined}
        />
      )}

      {/* Video — desktop only */}
      {!isMobile && (
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          crossOrigin="anonymous"
          className="absolute inset-0 w-full h-full object-cover"
        >
          {sources.map((s, i) => (
            <source key={i} src={s.src} type={s.type || "video/mp4"} />
          ))}
        </video>
      )}

      {/* Cinematic overlay gradient */}
      <div className={`absolute inset-0 ${overlayClassName} pointer-events-none`} />

      {/* Film grain — desktop only (mix-blend-mode is a mobile repaint bomb) */}
      {!isMobile && (
        <div className="absolute inset-0 pointer-events-none opacity-25 mix-blend-overlay"
             style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")" }} />
      )}
    </div>
  );
}

// Video sources — populated when self-hosted MP4s are dropped into /app/frontend/public/videos/.
// Kept empty by default: the poster image (with a desktop-only Ken Burns pan) is the design
// intent and avoids console noise from unreliable third-party CDNs.
export const HERO_SOURCES = [];
export const INTERLUDE_SOURCES = [];
